import { Calendar, ConfigProvider, Modal, notification, Spin } from 'antd';
import enUS from 'antd/locale/en_US';
import etEE from 'antd/locale/et_EE';
import ruRU from 'antd/locale/ru_RU';
import dayjs, { Dayjs } from 'dayjs';
import 'dayjs/locale/en';
import 'dayjs/locale/et';
import 'dayjs/locale/ru';
import updateLocale from 'dayjs/plugin/updateLocale';
import utc from 'dayjs/plugin/utc';
import React, { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { DATE_FORMAT } from '@/constants';
import { useGetAdditionalDischarges } from '@/features/objectItems/api/use-get-additional-discharges';
import { useGetObjectItemDischarges } from '@/features/objectItems/api/use-get-object-item-discharges';
import { useOrderAdditionalDischarge } from '@/features/objectItems/api/use-order-additional-discharge';

import { ChevronLeft, ChevronRight, Plus } from '@untitled-ui/icons-react';

import { formatAsCurrency } from '@/helpers/number-formatter';

import { useQueryClient } from '@tanstack/react-query';

import './WasteCalendar.css';

import { useCancelDischarge } from '@/features/objectItems/api/use-cancel-discharge';
import { cn } from '@/utils/cn';

dayjs.extend(utc);
dayjs.extend(updateLocale);
dayjs.updateLocale('en', { weekStart: 1 });

type Props = {
  objectItemId: number;
  isModalOpen: boolean;
  handleCancel: () => void;
};

const ObjectItemDischargesModal = ({ isModalOpen, handleCancel, objectItemId }: Props) => {
  const { t, i18n } = useTranslation('wasteCalendar');
  const [currentMonth, setCurrentMonth] = useState<Date | string>(new Date());
  const [selectedDate, setSelectedDate] = useState<string>();

  const [isDetailsModalVisible, setIsDetailsModalVisible] = useState(false);
  const queryClient = useQueryClient();

  const todayDate = useMemo(() => new Date().toISOString(), []);
  const currentLanguage = i18n.language as 'et' | 'en' | 'ru';

  const localeMap = {
    en: { dayjs: 'en', antd: enUS },
    ru: { dayjs: 'ru', antd: ruRU },
    et: { dayjs: 'et', antd: etEE },
  };

  const objectItemDischargesQuery = useGetObjectItemDischarges({
    params: {
      objectItemIds: [objectItemId],
      disablePagination: true,
      isActive: true,
      startDate: todayDate,
      isDeleted: false,
    },
    queryConfig: {
      enabled: !!objectItemId,
    },
  });

  const additionalDischargesQuery = useGetAdditionalDischarges({
    params: {
      objectItemId,
    },
    queryConfig: {
      enabled: !!objectItemId,
    },
  });

  const orderAdditionalDischargeMutation = useOrderAdditionalDischarge();
  const cancelDischargeMutation = useCancelDischarge();

  const dischargeDates = objectItemDischargesQuery.data?.map((discharge) => dayjs(discharge.date).format(DATE_FORMAT)) || [];
  const additionalDischargeDates =
    additionalDischargesQuery.data?.map(({ date, successPriceWithVat }) => ({
      date: dayjs(date).format(DATE_FORMAT),
      price: successPriceWithVat,
    })) || [];

  const handleMonthChange = (direction: 'prev' | 'next') => {
    if (direction === 'prev') {
      setCurrentMonth(dayjs(currentMonth).subtract(1, 'month').toDate());
    } else {
      setCurrentMonth(dayjs(currentMonth).add(1, 'month').toDate());
    }
  };

  const isDischargeDate = selectedDate && dischargeDates.includes(dayjs(selectedDate).format(DATE_FORMAT));

  const getPriceByDate = (date: string) => {
    let price;

    if (isDischargeDate) {
      price = objectItemDischargesQuery.data.find((discharge) => discharge.date === date)?.priceWithVat;
    } else {
      price = additionalDischargeDates.find(
        (additionalDischargeDate) => additionalDischargeDate.date === dayjs(date).format(DATE_FORMAT),
      )?.price;
    }

    return formatAsCurrency(Number(price) || 0);
  };

  const handleDateSelect = (date: Dayjs) => {
    setSelectedDate(dayjs(date).format('YYYY-MM-DD'));
    setIsDetailsModalVisible(true);
  };

  const renderDate = (date: Dayjs) => {
    const formattedDate = dayjs(date).format(DATE_FORMAT);
    const additionalDischargeDate = additionalDischargeDates.find(({ date }) => date === formattedDate);
    const isDischargeDate = dischargeDates.includes(formattedDate);

    const discharge = objectItemDischargesQuery.data?.find((discharge) => discharge.date === dayjs(date).format('YYYY-MM-DD'));

    let dateElement = (
      <div className="h-full w-full flex items-center justify-center">
        <span className="text-[#B9B9B9]">{dayjs(date).format('D')}</span>
      </div>
    );

    if (additionalDischargeDate) {
      dateElement = (
        <div className="h-full text-gray-10 font-medium w-full px-[6px] flex flex-col items-center justify-center bg-[#FB923C] rounded-[8px]">
          <span>{dayjs(date).format('D')}</span>
          <span className="bg-[#FFB273] rounded-[4px] w-full text-center">
            {formatAsCurrency(Number(additionalDischargeDate?.price) || 0)}
          </span>
        </div>
      );
    }

    if (isDischargeDate) {
      dateElement = (
        <div className="h-full text-gray-10 font-medium w-full px-[6px] flex flex-col items-center justify-center bg-[#D5DC2E] rounded-[8px]">
          <div className="flex w-full justify-between">
            <span className="w-5 h-5" />
            <span className="text-gray-10 font-medium">{dayjs(date).format('D')}</span>
            {discharge.isExtraDischarge ? <Plus className="h-5 w-5 text-[#A7AD2A]" /> : <span className="w-5 h-5" />}
          </div>
          <span className="bg-[#EBF163] rounded-[4px] w-full text-center">{formatAsCurrency(Number(discharge.priceWithVat) || 0)}</span>
        </div>
      );
    }

    return (
      <div title={dayjs(date).format(DATE_FORMAT)} className="w-full border border-[#EFEFEF] sm:h-[77px] h-[83px] px-[3px] py-[4px]">
        {dateElement}
      </div>
    );
  };

  const handleOrderAdditionalDischarge = () => {
    orderAdditionalDischargeMutation.mutate(
      {
        objectItemId,
        date: selectedDate,
      },
      {
        onSuccess: async () => {
          setIsDetailsModalVisible(false);
          notification.success({
            message: t('additionalDischargeOrdered'),
          });

          await queryClient.invalidateQueries({
            predicate: (query) => ['object-item-discharges'].includes((query.queryKey as string[])[0]),
          });
        },
        onError: async () => {
          setIsDetailsModalVisible(false);
          notification.error({
            message: t('somethingWentWrong', { ns: 'common' }),
          });
        },
      },
    );
  };

  const handleCancelDischarge = () => {
    const discharge = objectItemDischargesQuery.data?.find((discharge) => discharge.date === selectedDate);

    cancelDischargeMutation.mutate(discharge.id, {
      onSuccess: async () => {
        setIsDetailsModalVisible(false);
        notification.success({
          message: t('dischargeCancelled'),
        });

        await queryClient.invalidateQueries({
          predicate: (query) => ['object-item-discharges', 'additional-discharges'].includes((query.queryKey as string[])[0]),
        });
      },
      onError: async () => {
        setIsDetailsModalVisible(false);
        notification.error({
          message: t('somethingWentWrong', { ns: 'common' }),
        });
      },
    });
  };

  const disabledDate = (current: Dayjs) => {
    const formattedDate = current.format(DATE_FORMAT);
    const isDischargeDate = dischargeDates.includes(formattedDate);
    const additionalDischargeDate = additionalDischargeDates.find(({ date }) => date === formattedDate);

    const discharge = objectItemDischargesQuery.data?.find((discharge) => discharge.date === current.format('YYYY-MM-DD'));

    return !((isDischargeDate && discharge.isExtraDischarge) || additionalDischargeDate);
  };

  return (
    <Modal
      centered
      styles={{
        body: {
          maxHeight: '100vh',
        },
        content: {
          justifySelf: 'center',
          borderRadius: 24,
        },
      }}
      width={window.innerWidth < 744 ? '100%' : 880}
      open={isModalOpen}
      onCancel={handleCancel}
      title={
        <div className="flex gap-4 items-center text-[24px] text-bold">
          <span className="text-gray-10">{t('placingOrder')}</span>
        </div>
      }
      footer={false}
    >
      <ConfigProvider locale={localeMap[currentLanguage]?.antd || enUS}>
        <Spin
          spinning={
            objectItemDischargesQuery.isFetching || additionalDischargesQuery.isFetching || orderAdditionalDischargeMutation.isPending
          }
        >
          <Calendar
            onSelect={handleDateSelect}
            disabledDate={disabledDate}
            headerRender={() => (
              <div className="flex h-[52px] justify-between items-center border-b border-b-[#F0F0F0]">
                <span className="text-[20px] font-semibold">
                  {dayjs(currentMonth)
                    .locale(currentLanguage)
                    .format('MMMM YYYY')
                    .replace(/^\w/, (c) => c.toUpperCase())}
                </span>
                <div className="flex gap-2">
                  <button
                    onClick={() => handleMonthChange('prev')}
                    className="bg-[#D5DC2E] h-9 w-9 rounded-[8px] outline-none flex items-center justify-center"
                  >
                    <ChevronLeft className="text-gray-10 stroke-2 w-4 h-4" />
                  </button>
                  <button
                    onClick={() => handleMonthChange('next')}
                    className="bg-[#D5DC2E] h-9 w-9 rounded-[8px] outline-none flex items-center justify-center"
                  >
                    <ChevronRight className="text-gray-10 stroke-2 w-4 h-4" />
                  </button>
                </div>
              </div>
            )}
            fullCellRender={renderDate}
            value={dayjs(currentMonth)}
          />
        </Spin>
        <div className="flex gap-4 mt-4 justify-between">
          <div className="flex gap-3 items-center">
            <span className="w-[56px] h-[32px] rounded-[8px] bg-[#D5DC2E]" />
            <span className="text-gray-10 font-medium text-base">{t('scheduledForContainerEmptying')}</span>
          </div>
          <div className="flex gap-3 items-center">
            <span className="w-[56px] h-[32px] rounded-[8px] bg-[#FB923C]" />
            <span className="text-gray-10 px-[3px] py-[3px] font-medium text-base">{t('additionalOrderAvailable')}</span>
          </div>
        </div>
        <Modal
          open={isDetailsModalVisible}
          loading={orderAdditionalDischargeMutation.isPending || cancelDischargeMutation.isPending}
          onCancel={() => setIsDetailsModalVisible(false)}
          onClose={() => setIsDetailsModalVisible(false)}
          styles={{
            content: {
              width: '100%',
              justifySelf: 'center',
              borderRadius: 24,
            },
          }}
          title={
            <div className="flex gap-4 items-center text-[24px] text-bold">
              <span className="text-gray-10">{isDischargeDate ? t('doYouWantToCancel') : t('orderExtraDischarge')}</span>
            </div>
          }
          footer={
            <div className="gap-4 flex">
              <button
                disabled={orderAdditionalDischargeMutation.isPending}
                onClick={() => setIsDetailsModalVisible(false)}
                className="outline-none text-white flex-1 h-12 bg-[#B9B9B9] rounded-[14px] flex justify-center items-center"
              >
                {t('no', { ns: 'common' })}
              </button>
              <button
                disabled={orderAdditionalDischargeMutation.isPending}
                onClick={isDischargeDate ? handleCancelDischarge : handleOrderAdditionalDischarge}
                className={cn(
                  'outline-none text-gray-10 flex-1 h-12 rounded-[14px] flex justify-center items-center',
                  isDischargeDate ? 'bg-[#F97315] text-white' : 'bg-[#D5DC2E]',
                )}
              >
                {t('yes', { ns: 'common' })}
              </button>
            </div>
          }
        >
          <div className="flex gap-2 mb-3 items-center">
            <span>{t('selectedDate')}</span>
            <span className="py-1 px-4 rounded-[8px] bg-[#EBF163] text-gray-10">{dayjs(selectedDate).format(DATE_FORMAT)}</span>
          </div>
          <div className="flex gap-2 mb-3 items-center">
            <span>{isDischargeDate ? t('scheduledForContainerEmptying') : t('additionalOrderAvailable')}</span>
            <span className={cn('py-1 px-4 rounded-[8px]', isDischargeDate ? 'bg-[#EBF163] text-gray-10' : 'bg-[#FB923C] text-white')}>
              {getPriceByDate(selectedDate)}
            </span>
          </div>
        </Modal>
      </ConfigProvider>
    </Modal>
  );
};

export default ObjectItemDischargesModal;
